import { useTranslation } from 'react-i18next'

import { EMPTY_LIST, EMPTY_OBJECT } from '../../../constants/common'
import { SUBMENU_DEFAULT_VALUE } from '../../../constants/route'
import { FilterBar } from '../../Filter'
import { FilterDivider } from '../../FilterDivider'
import { PageActions } from '../../Page'
import { PageEmptyState } from '../../PageEmptyState'
import { Pagination } from '../../Pagination'
import { SelectionToolbar } from '../../SelectionToolbar'
import {
  getSelectedSize,
  getTableResetSortingButton,
  Table,
  TableConfigModal,
  TableControlBar,
  TableControlButtons,
  TableMassAction,
} from '../../Table'
import { TablePagination } from '../../TablePagination'
import { useDocFlowPhaseTable } from '../DocFlow.hooks'

import * as styles from './DocFlowPhaseTable.module.css'

import DocFlowIcon from '../../../icons/doc-splitting.svg'

function DocFlowPhaseTable({
  TableFilterBar = FilterBar,
  TableRow,
  className = styles.DocFlowPhaseTable,
  disableActions,
  emptyStateActions,
  emptyStateText,
  entity,
  getActions,
  getIsHiddenTableText,
  loadingTable,
  paginationClassName = styles.pagination,
  selectionData = EMPTY_OBJECT,
  setItemAction,
  setMassAction,
  tableLoader,
  tableProps,
  tableRowProps,
  ...restProps
}) {
  const { t } = useTranslation()

  const {
    containerRef,
    documentId,
    filterApi,
    handleConfigOpen,
    handleUpdateConfig,
    isConfigOpen,
    massActions,
    phaseDisabled,
    refreshButtonProps,
    setConfigOpen,
    tableItems,
  } = useDocFlowPhaseTable({
    ...restProps,
    TableFilterBar,
    tableLoader,
  })

  const {
    availableFilters,
    columns,
    filterValues,
    handleResetOrdering,
    handleSetColumns,
    handleSetFilterValues,
    handleSetOrdering,
    handleSetPagination,
    handleSetSize,
    isInitiated,
    isLoading,
    ordering,
    pagination,
    permissions,
    requiredColumns,
  } = tableLoader

  const {
    checkIsItemSelected,
    exclude,
    handleExclude,
    handleSelect,
    handleSelectAll,
    handleSelectAllPages,
    handleUnselectAllPages,
    isAllSelected,
    isRemoveSelection,
    selectAllPages,
    selected,
  } = selectionData

  const isInitiating = loadingTable || (!isInitiated && isLoading)

  return (
    <div className={className}>
      {availableFilters.length > 0 && TableFilterBar ? (
        <TableFilterBar
          responsive
          apiUrl={filterApi}
          availableFilters={availableFilters}
          className={styles.tableFilters}
          entity={entity}
          excludeFilters={EMPTY_LIST}
          filterValues={filterValues}
          isSkeleton={loadingTable || isLoading}
          objectId={documentId}
          ordering={ordering}
          pagination={{ per_page: pagination.pageSize }}
          setFilterValues={handleSetFilterValues}
          sub={SUBMENU_DEFAULT_VALUE}
        />
      ) : null}
      {isLoading || (!isLoading && tableItems.length > 0) ? (
        <div className={styles.tableWrapper}>
          <TableControlBar className={styles.controlBar}>
            {massActions.length > 0 ? (
              <TableMassAction
                responsive
                actions={massActions}
                disabled={phaseDisabled || disableActions}
                getIsHiddenTableText={getIsHiddenTableText}
                isAllSelected={isAllSelected}
                isLoading={loadingTable || isLoading}
                isRemoveSelection={isRemoveSelection}
                selectedSize={getSelectedSize({
                  exclude,
                  pagination,
                  selectAllPages,
                  selected,
                })}
                setMassAction={setMassAction}
                onSelectAll={handleSelectAll}
              />
            ) : (
              <div />
            )}
            <PageActions isLoading={isInitiating}>
              <TableControlButtons
                DensityButton={null}
                ResetSortingButton={getTableResetSortingButton(
                  ordering || EMPTY_LIST
                )}
                configButtonProps={{ onClick: handleConfigOpen }}
                refreshButtonProps={refreshButtonProps}
                resetSortingProps={{ onClick: handleResetOrdering }}
              />
              <FilterDivider />
              <Pagination
                responsive
                getIsHiddenTableText={getIsHiddenTableText}
                value={pagination}
                onChange={handleSetPagination}
                onChangeSize={handleSetSize}
              />
            </PageActions>
          </TableControlBar>
          <Table
            {...tableProps}
            TableRow={TableRow}
            checkIsItemSelected={checkIsItemSelected}
            columns={columns}
            containerRef={containerRef}
            headerRowProps={{
              ordering,
              onApply: handleUpdateConfig,
              onSetColumns: handleSetColumns,
              onSetOrdering: handleSetOrdering,
            }}
            isLoading={isInitiating}
            items={tableItems}
            rowProps={{
              ...tableRowProps,
              exclude,
              getActions,
              hideCheckbox: massActions.length === 0 || disableActions,
              isSelectedAll: selectAllPages,
              permissions,
              phaseDisabled: phaseDisabled || disableActions,
              selected,
              onCallAction: setItemAction,
              onSelect: selectAllPages ? handleExclude : handleSelect,
            }}
          />
          <TablePagination
            responsive
            className={paginationClassName}
            isSkeleton={isInitiating}
            value={pagination}
            onChange={handleSetPagination}
          />
        </div>
      ) : (
        <PageEmptyState
          EntityIcon={DocFlowIcon}
          actions={emptyStateActions}
          text={t(emptyStateText)}
        />
      )}
      {isConfigOpen ? (
        <TableConfigModal
          columns={columns}
          entity={entity}
          requiredColumns={requiredColumns}
          setColumns={handleSetColumns}
          setConfigOpen={setConfigOpen}
          onApply={handleUpdateConfig}
        />
      ) : null}
      {selected?.length > 0 || selectAllPages ? (
        <SelectionToolbar
          allSelected={selectAllPages || selected.length === pagination.count}
          count={pagination.count}
          excludeCount={exclude.length}
          selected={selected.length}
          onClose={handleUnselectAllPages}
          onSelectAll={handleSelectAllPages}
        />
      ) : null}
    </div>
  )
}

export default DocFlowPhaseTable
